/**
 * CostingTab — Read-only WAC and cost history — Tab 6 of the Item form.
 * All data is calculated by the backend on GRN receipt; no edit fields.
 *
 * Props:
 *   itemId  string  — item UUID; null means item not yet saved
 *
 * API:
 *   TODO: GET /inventory/items/{itemId}/cost-history/  (getItemCostHistory)
 *   Mock: hardcoded cost data and history until backend endpoint exists
 *
 * Junior developer note:
 *   WAC (Weighted Average Cost) is recalculated on every GRN by the backend.
 *   Never allow manual edits here — changes must flow through GRN entries.
 */
import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Alert, Skeleton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { DataGrid } from '@mui/x-data-grid';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { IPD_COLORS, tableContainerSx, cardSx } from '../../theme';
import { getItemCostHistory } from '../../api/itemCosting';

// ── Stat card sub-component ──────────────────────────────────────
function CostStatCard({ label, value, sub }) {
  return (
    <Box sx={{
      ...cardSx,
      p: 2,
      textAlign: 'center',
    }}>
      <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11, textTransform: 'uppercase',
        letterSpacing: '0.06em', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 22, fontWeight: 700 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11, mt: 0.25 }}>{sub}</Typography>
      )}
    </Box>
  );
}

export default function CostingTab({ itemId }) {
  const [costData, setCostData]   = useState(null);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  async function load() {
    if (!itemId) return;
    setLoading(true);
    setError(null);
    try {
      // TODO: replace mock with: const data = await getItemCostHistory(itemId);
      // Mock data until backend endpoint exists:
      setCostData({
        wac:           0.84,
        lastPurchase:  0.90,
        method:        'WAC',
        lastUpdated:   '2026-06-12',
        baseUomCode:   'TAB',
      });
      setHistory([
        { id: '1', date: '2026-06-12', trigger: 'GRN',  reference: 'GRN-2024-0312',
          oldWac: 0.80, newWac: 0.84, qtyAdded: 5000, unit: 'TAB', by: 'Ravi K.' },
      ]);
    } catch (err) {
      setError(err.message || 'Failed to load cost history');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [itemId]);

  // ── DataGrid columns ─────────────────────────────────────────
  const columns = [
    { field: 'date',      headerName: 'Date',       width: 110 },
    { field: 'trigger',   headerName: 'Trigger',    width: 90 },
    { field: 'reference', headerName: 'Reference',  flex: 1, minWidth: 140 },
    { field: 'oldWac',    headerName: 'Old WAC',    width: 90,  align: 'right', headerAlign: 'right',
      renderCell: p => <Typography sx={{ fontSize: 13 }}>₹{p.value?.toFixed(4)}</Typography> },
    { field: 'newWac',    headerName: 'New WAC',    width: 90,  align: 'right', headerAlign: 'right',
      renderCell: p => <Typography sx={{ fontSize: 13, color: IPD_COLORS.primary }}>₹{p.value?.toFixed(4)}</Typography> },
    { field: 'qtyAdded',  headerName: 'Qty Added',  width: 100, align: 'right', headerAlign: 'right',
      renderCell: p => <Typography sx={{ fontSize: 13 }}>{p.value?.toLocaleString()} {p.row.unit}</Typography> },
    { field: 'by',        headerName: 'By',         width: 120 },
  ];

  // ── Guard ────────────────────────────────────────────────────
  if (!itemId) {
    return <Alert severity="info">Save the item first to view costing data.</Alert>;
  }

  // ── Loading skeleton ─────────────────────────────────────────
  if (loading && !costData) {
    return (
      <Grid container spacing={2.5}>
        {[1, 2, 3].map(i => (
          <Grid item xs={12} sm={4} key={i}>
            <Skeleton variant="rectangular" height={88} sx={{ borderRadius: '12px' }} />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
        </Grid>
      </Grid>
    );
  }

  // ── Error state ──────────────────────────────────────────────
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}
        action={<InvButton size="small" variant="outlined" onClick={load}>Retry</InvButton>}>
        {error}
      </Alert>
    );
  }

  // ── Render ───────────────────────────────────────────────────
  return (
    <Grid container spacing={2.5}>
      {/* ── Stat cards ─────────────────────────── */}
      <Grid item xs={12} sm={4}>
        <CostStatCard
          label="Weighted Avg Cost"
          value={costData ? `₹${costData.wac.toFixed(4)}` : '—'}
          sub={costData ? `per ${costData.baseUomCode}` : null}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CostStatCard
          label="Last Purchase Price"
          value={costData ? `₹${costData.lastPurchase.toFixed(4)}` : '—'}
          sub={costData ? `per ${costData.baseUomCode}` : null}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CostStatCard
          label="Valuation Method"
          value={costData?.method || '—'}
          sub={costData ? `Last updated ${costData.lastUpdated}` : null}
        />
      </Grid>

      {/* ── History grid ───────────────────────── */}
      <Grid item xs={12}>
        <SectionCard
          title="Cost History"
          actions={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ color: IPD_COLORS.textSecondary, fontSize: 18 }} />
              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>Read-only</Typography>
            </Box>
          }
        >
          <Box sx={{ ...tableContainerSx, ...cardSx, border: 'none', boxShadow: 'none' }}>
            <DataGrid
              rows={history}
              columns={columns}
              loading={loading}
              autoHeight
              getRowId={r => r.id}
              hideFooter={history.length <= 10}
              disableRowSelectionOnClick
              sx={{ border: 'none', fontSize: 13 }}
              slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
            />
          </Box>
          {!loading && history.length === 0 && (
            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13, textAlign: 'center', py: 3 }}>
              No cost history yet. History is recorded automatically on GRN receipt.
            </Typography>
          )}
        </SectionCard>
      </Grid>
    </Grid>
  );
}
